import { type MissionValidationError, validateMission, type Mission } from './schema';

/**
 * Loading and cataloguing missions (SPEC §3.10, M8).
 *
 * Vite's `import.meta.glob` with `eager` pulls every mission JSON into the bundle at build time,
 * so a mission is a static asset rather than a fetch: no load spinner, no network failure mode,
 * and — the reason that matters — a malformed mission is caught the moment the catalogue is built
 * rather than when the player picks it.
 *
 * Dev hot reload comes free with the same mechanism: Vite invalidates the module on write, and the
 * runner listens for that to restart the mission in place (SPEC's M8 acceptance criterion).
 */

/** A mission that failed validation, kept so the catalogue can report rather than hide it. */
export interface BrokenMission {
  readonly path: string;
  readonly error: MissionValidationError | Error;
}

export interface MissionCatalogue {
  /** Valid campaign missions, ordered by `order`. */
  readonly campaign: readonly Mission[];
  readonly broken: readonly BrokenMission[];
  byId(id: string): Mission | null;
  /** The next mission after `id`, or null at the end of the campaign. */
  next(id: string): Mission | null;
}

/**
 * Build a catalogue from a map of path → parsed JSON.
 *
 * Kept separate from the glob so tests can build a catalogue from literals without touching the
 * filesystem or Vite.
 */
export function buildCatalogue(modules: Record<string, unknown>): MissionCatalogue {
  const campaign: Mission[] = [];
  const broken: BrokenMission[] = [];

  for (const path of Object.keys(modules).sort()) {
    const raw = unwrapModule(modules[path]);
    try {
      campaign.push(validateMission(raw));
    } catch (error) {
      broken.push({
        path,
        error: error instanceof Error ? error : new Error(String(error)),
      });
    }
  }

  campaign.sort((a, b) => a.order - b.order);

  const index = new Map<string, Mission>();
  for (let i = 0; i < campaign.length; i += 1) index.set(campaign[i].id, campaign[i]);

  // Duplicate order numbers make "next mission" ambiguous, which shows up as a campaign that
  // silently skips or repeats — worth shouting about rather than sorting arbitrarily.
  const orders = new Set<number>();
  for (let i = 0; i < campaign.length; i += 1) {
    if (orders.has(campaign[i].order)) {
      broken.push({
        path: campaign[i].id,
        error: new Error(`duplicate mission order ${campaign[i].order}`),
      });
    }
    orders.add(campaign[i].order);
  }

  return {
    campaign,
    broken,
    byId: (id) => index.get(id) ?? null,
    next: (id) => {
      const at = campaign.findIndex((mission) => mission.id === id);
      if (at < 0 || at + 1 >= campaign.length) return null;
      return campaign[at + 1];
    },
  };
}

/** ES module namespaces arrive as `{ default: ... }`; raw JSON does not. */
function unwrapModule(module: unknown): unknown {
  if (typeof module === 'object' && module !== null && 'default' in module) {
    return (module as { default: unknown }).default;
  }
  return module;
}

/**
 * The campaign as shipped.
 *
 * `eager: true` because twelve small JSON files are a few kilobytes total, and paying for them at
 * build time removes an entire class of runtime failure from the mission-select screen.
 */
export function loadCampaign(): MissionCatalogue {
  const modules = import.meta.glob('./missions/*.json', { eager: true });
  return buildCatalogue(modules);
}

/**
 * Report broken missions loudly.
 *
 * A mission that fails to load is a content bug, and content bugs that log quietly get shipped.
 * The full issue list goes to the console because that is where the person who can fix it looks.
 */
export function reportBroken(catalogue: MissionCatalogue): void {
  for (let i = 0; i < catalogue.broken.length; i += 1) {
    const entry = catalogue.broken[i];
    console.error(`MISSION FAILED TO LOAD — ${entry.path}\n${entry.error.message}`);
  }
}
